import type { ReactElement } from 'react';

import { type Theme, useTheme } from '@emotion/react';
import { isNil } from 'ramda';

import { useDesignSystem } from '@twistopayments/design-system/base';
import type { CssClasses } from '@twistopayments/design-system/styles';
import type { RequireOnlyOne } from '@twistopayments/design-system/utils';

import { AvatarSubIcon } from './avatar-sub-icon';
import * as styles from './avatar.styles';

type CssClassesApi = Pick<typeof styles, 'root' | 'children' | 'subIcon'>;

export type AvatarProps = RequireOnlyOne<
  CssClasses<CssClassesApi> & {
    /** Static image asset (e.g. Next.js StaticImageData) — rendered via the
     *  host-provided ImageComponent when available, otherwise as `<img>`. */
    image: string | ImageMeta;
    src: string;
    alt?: string;
    subIcon?: ReactElement;
    children?: ReactElement;
    size?: styles.AvatarSizes;
    noPlaceholder?: boolean;
    noBorder?: boolean;
  },
  'image' | 'src' | 'children'
>;

export const Avatar = ({
  image,
  alt,
  src,
  subIcon,
  children,
  styles: stylesProp = {},
  className,
  size = '40',
  noPlaceholder,
  noBorder = false,
  ...other
}: AvatarProps) => {
  const theme = useTheme();
  const { ImageComponent } = useDesignSystem();
  const Img = ImageComponent ?? 'img';

  const rootStyles = (theme: Theme) => [
    styles.root(theme),
    styles.size(size),
    !!subIcon && styles.spacingRight(theme),
    (noPlaceholder ?? false) && styles.noPlaceholderBackground,
    stylesProp.root,
  ];

  const imageStyles = (theme: Theme) => [
    styles.mainIconStyle(theme, noBorder),
    styles.image(theme),
  ];

  const chidlrenWrapperStyles = (theme: Theme) => [
    styles.mainIconStyle(theme, noBorder),
    styles.children,
    stylesProp.children,
  ];

  return (
    <div className={className} css={rootStyles(theme)} data-testid="avatar">
      {src !== undefined && alt !== undefined && (
        <Img
          alt={alt}
          css={imageStyles(theme)}
          height={size}
          src={src}
          width={size}
          {...other}
        />
      )}
      {image !== undefined && !isNil(alt) && (
        <Img
          alt={alt}
          height={size}
          src={image as string}
          width={size}
        />
      )}
      {subIcon && (
        <AvatarSubIcon css={[styles.subIcon(theme), stylesProp.subIcon]}>{subIcon}</AvatarSubIcon>
      )}
      {children && <div css={chidlrenWrapperStyles(theme)}>{children}</div>}
    </div>
  );
};
